import React from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";

import { personInformationActions } from "@app/reducers/personInformationReducer";
import getSubscription from "@app/services/subscription/getSubscription";

import { useUser } from "@contexts/AuthProvider";

import { useScrollToTop } from "@hooks";

import { CUSTOMER } from "@utils/constant";

import { isTokenValid } from "./Login.utils";
import { useLoginSharedData } from "./hooks";

export const useLogin = () => {
  const dispatch = useDispatch();
  const { messages } = useIntl();
  const { setErrorCallback, setSuccessCallback } = useLoginSharedData();
  useScrollToTop();
  const { initializeUser } = useUser();

  const [bankIdLoginError, setBankIdLoginError] = React.useState("");

  const isLoginErrorDialogVisible = React.useMemo(
    () => bankIdLoginError !== "",
    [bankIdLoginError]
  );

  const redirectToPage = React.useCallback(
    async user => {
      const { customer: { id = "" } = {}, type = "" } = user;

      if (type == CUSTOMER && id) {
        const { data: { data: subscriptionData = [] } = {} } =
          await getSubscription(id);
        const [activeSubscription = false] = subscriptionData;

        dispatch(
          personInformationActions.setHasActiveSubscription(activeSubscription)
        );
      }
    },
    [dispatch]
  );

  const handleError = React.useCallback(
    error => {
      if (error === messages.message_bankID_unknown_error) {
        setBankIdLoginError(messages.bank_id_login_error_content);
        return;
      }

      setBankIdLoginError(error);
    },
    [
      messages.bank_id_login_error_content,
      messages.message_bankID_unknown_error,
    ]
  );

  const handleCloseErrorDialog = React.useCallback(() => {
    setBankIdLoginError("");
  }, []);

  const handleLoginSubmit = React.useCallback(
    ({ token, user }) => {
      if (!isTokenValid(token)) {
        handleError(messages.invalid_data);
        return;
      }

      localStorage.setItem("token", token);

      initializeUser(user);
      redirectToPage(user);
    },
    [handleError, initializeUser, messages.invalid_data, redirectToPage]
  );

  React.useEffect(() => {
    setErrorCallback(handleError);
    setSuccessCallback(handleLoginSubmit);
  }, [handleError, handleLoginSubmit, setErrorCallback, setSuccessCallback]);

  return {
    bankIdLoginError,
    isLoginErrorDialogVisible,
    handleCloseErrorDialog,
    handleLoginSubmit,
  };
};
